export const TRAVELLER_CLASS_PREFIX = 'tc_';
export const PRODUCT_CLASS_PREFIX = 'pc_';
export const TRAVELLER_CLASS_ADULT = 'tc_a';
// Group tickets bundle several traveller classes, so the backend can't derive a
// single traveller_class and stores it as this sentinel instead.
const UNKNOWN_CLASS = 'unknown';

export interface TicketClassInput {
  product_class?: string;
  traveller_class?: string;
  tags?: string[];
}

const parseClassPart = (part: string): { base: string; quantity: number } => {
  const [rawBase, rawQuantity] = part.split('*');
  const base = rawBase.trim();

  if (rawQuantity === undefined) {
    return { base, quantity: 1 };
  }

  const quantity = parseInt(rawQuantity.trim(), 10);
  return { base, quantity: Number.isNaN(quantity) ? 1 : quantity };
};

// The `tags` array is the source of truth and the only place that keeps every
// class for group tickets; the derived `traveller_class` field is "unknown" when
// a ticket bundles multiple classes. Fall back to the field for older tickets.
const resolveTravellerParts = (ticket: TicketClassInput): string[] => {
  const fromTags = (ticket.tags ?? []).filter((tag) =>
    tag.startsWith(TRAVELLER_CLASS_PREFIX),
  );

  if (fromTags.length > 0) {
    return fromTags;
  }

  return (ticket.traveller_class ?? '')
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.startsWith(TRAVELLER_CLASS_PREFIX));
};

export const getTravellerClassTags = (ticket: TicketClassInput): string[] => {
  const bases = resolveTravellerParts(ticket).map(
    (part) => parseClassPart(part).base,
  );
  return Array.from(new Set(bases));
};

export const formatTravellerClass = (
  ticket: TicketClassInput,
  tagTitles: Record<string, string>,
): string => {
  const quantities = new Map<string, number>();
  resolveTravellerParts(ticket).forEach((part) => {
    const { base, quantity } = parseClassPart(part);
    quantities.set(base, (quantities.get(base) ?? 0) + quantity);
  });

  const entries = Array.from(quantities.entries()).sort(([a], [b]) =>
    a.localeCompare(b),
  );

  const totalTravellers = entries.reduce(
    (sum, [, quantity]) => sum + quantity,
    0,
  );
  const showQuantity = totalTravellers > 1;

  return entries
    .map(([base, quantity]) => {
      const title = tagTitles[base] ?? base;
      return showQuantity ? `${quantity}x ${title}` : title;
    })
    .join(', ');
};

const normalizedClass = (value?: string): string => value?.trim() ?? '';

const isUnknownClass = (value?: string): boolean =>
  normalizedClass(value).toLowerCase() === UNKNOWN_CLASS;

// Stored class fields are display text. Tag ids (`pc_*`, `tc_*`) and the
// "unknown" sentinel still have to be resolved from tags.
const isClassTagList = (value: string): boolean => {
  const parts = value
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);

  if (parts.length === 0) return false;

  return parts.every((part) => {
    const base = part.split('*')[0]?.trim() ?? '';
    return (
      base.startsWith(PRODUCT_CLASS_PREFIX) ||
      base.startsWith(TRAVELLER_CLASS_PREFIX)
    );
  });
};

const storedClassText = (value?: string): string | undefined => {
  const normalized = normalizedClass(value);
  if (!normalized || isUnknownClass(normalized)) return undefined;
  if (isClassTagList(normalized)) return undefined;
  return normalized;
};

const resolveProductClass = (ticket: TicketClassInput): string | undefined => {
  const productClass = normalizedClass(ticket.product_class);
  if (productClass && !isUnknownClass(productClass)) {
    return productClass;
  }

  return (ticket.tags ?? []).find((tag) =>
    tag.startsWith(PRODUCT_CLASS_PREFIX),
  );
};

const formatProductClass = (
  ticket: TicketClassInput,
  tagTitles: Record<string, string>,
): string => {
  const productClass = resolveProductClass(ticket);
  return productClass ? (tagTitles[productClass] ?? productClass) : '';
};

export const formatTicketClass = (
  ticket: TicketClassInput,
  tagTitles: Record<string, string>,
): string => {
  const productTitle = formatProductClass(ticket, tagTitles);
  const travellerLabel = formatTravellerClass(ticket, tagTitles);

  return [productTitle, travellerLabel].filter(Boolean).join(' • ');
};

export const formatIssuedTicketClass = (
  ticket: TicketClassInput,
  tagTitles: Record<string, string>,
): string => {
  const productLabel =
    storedClassText(ticket.product_class) ??
    formatProductClass(ticket, tagTitles);
  const travellerLabel =
    storedClassText(ticket.traveller_class) ??
    formatTravellerClass(ticket, tagTitles);

  return [productLabel, travellerLabel].filter(Boolean).join(' • ');
};
